import React from "react";
import Layout from "../components/Layout";

const About = () => {
  return (
    <Layout>
      <div className="about-hero">
        <div className="container">
          <h1 className="section-title-modern">About Me</h1>
          <p className="section-subtitle-modern">
            Linguistics, field research, and AI engineering
          </p>
        </div>
      </div>

      <div className="about-content">
        <div className="container">
          <div className="about-grid">
            <div className="about-text">
              <h2>From Languages to Systems</h2>
              <p>
                My background spans economics and international politics, but
                the real education came from working across continents—teaching
                Business English to executives in South America, serving as a
                village schoolteacher in the Himalayas, conducting field
                research on smallholder agriculture in rural Africa. Different
                contexts, same core skill: communicating precisely in
                environments where ambiguity has real consequences.
              </p>
              <p>
                That obsession with linguistic precision—grammar, translation,
                the mechanics of moving meaning across boundaries—led naturally
                to computational linguistics and a master's in applied data
                science from the University of Michigan. From there, AI software
                engineering. I've since built ML and AI systems for Fortune 500
                companies, with a focus on the gap between what these tools
                promise and what they actually deliver.
              </p>
              <p>
                Today I consult on practical AI implementation—designing systems
                that work in production, not demos that impress in a boardroom.
                My focus areas include misinformation research, education
                technology, and building for AI-enabled humans rather than
                waiting for AGI to arrive. If you're solving real problems in
                this space, let's talk.
              </p>
            </div>

            <div className="about-images">
              <img
                src="/myphotos/bros.png"
                alt="Cultural connection"
                className="about-img"
              />
              <img
                src="/myphotos/mongoliateacher.jpeg"
                alt="Teaching in Mongolia"
                className="about-img"
              />
              <img
                src="/myphotos/tshewang.jpg"
                alt="International experience"
                className="about-img"
              />
              <img
                src="/myphotos/editor.jpg"
                alt="Teaching in Mongolia"
                className="about-img"
              />
            </div>
          </div>

          <div className="expertise-section">
            <h2>What I Actually Do</h2>
            <p>
              I help organizations ship AI products that create real value. That
              means working across both layers of the problem—the technical
              stack (LLM integration, fine-tuning, orchestration, production ML
              systems) and the strategic questions underneath it: What problem
              are we actually solving? What does success look like, and how do
              we measure it honestly?
            </p>
            <p>
              The through-line in my background is translation—not just
              linguistic, but contextual. Years working internationally mean
              I've built systems that handle multilingual content from the
              ground up and managed distributed teams across time zones. The
              educator background means I can bridge technical and non-technical
              stakeholders without condescending to either. The linguistics
              training means I hold language models to a higher standard than
              benchmark performance—I care about how they actually handle
              language in the wild.
            </p>
            <p>
              The work I find most compelling sits at the intersection of AI and
              real-world impact: detecting misinformation at scale, building
              educational tools that genuinely extend access, designing systems
              that make people more capable rather than more dependent. If
              that's the kind of problem you're working on, I want to hear about
              it.
            </p>
          </div>

          <div className="skills-grid">
            <div className="skill-category">
              <h3>Technical Stack</h3>
              <div className="skill-list">
                <span className="skill-tag">LLM Integration & Fine-tuning</span>
                <span className="skill-tag">Production ML Systems</span>
                <span className="skill-tag">
                  NLP & Computational Linguistics
                </span>
                <span className="skill-tag">AI Orchestration & Workflows</span>
                <span className="skill-tag">Python, PyTorch, Transformers</span>
                <span className="skill-tag">Multilingual NLP</span>
              </div>
            </div>

            <div className="skill-category">
              <h3>Domain Expertise</h3>
              <div className="skill-list">
                <span className="skill-tag">Misinformation Research</span>
                <span className="skill-tag">Education Technology</span>
                <span className="skill-tag">Applied Linguistics</span>
                <span className="skill-tag">Political Economy</span>
                <span className="skill-tag">
                  Cross-cultural Product Development
                </span>
                <span className="skill-tag">Grammar & Translation Systems</span>
              </div>
            </div>

            <div className="skill-category">
              <h3>What Sets Me Apart</h3>
              <div className="skill-list">
                <span className="skill-tag">
                  Field research in remote contexts
                </span>
                <span className="skill-tag">
                  Business & village-level education
                </span>
                <span className="skill-tag">
                  Distributed team collaboration
                </span>
                <span className="skill-tag">
                  Technical-to-non-technical translation
                </span>
                <span className="skill-tag">
                  Shipping AI products, not demos
                </span>
              </div>
            </div>
          </div>

          <div className="languages-section">
            <h2>Languages</h2>
            <div className="language-bars">
              <div className="language-item">
                <span className="language-name">English</span>
                <div className="language-bar-container">
                  <div
                    className="language-bar language-bar-native"
                    style={{ width: "100%" }}
                  ></div>
                </div>
              </div>

              <div className="language-item">
                <span className="language-name">Français</span>
                <div className="language-bar-container">
                  <div
                    className="language-bar language-bar-professional"
                    style={{ width: "90%" }}
                  ></div>
                </div>
              </div>

              <div className="language-item">
                <span className="language-name">Español</span>
                <div className="language-bar-container">
                  <div
                    className="language-bar language-bar-professional"
                    style={{ width: "90%" }}
                  ></div>
                </div>
              </div>

              <div className="language-item">
                <span className="language-name">Português</span>
                <div className="language-bar-container">
                  <div
                    className="language-bar language-bar-conversational"
                    style={{ width: "70%" }}
                  ></div>
                </div>
              </div>

              <div className="language-item">
                <span className="language-name">日本語</span>
                <div className="language-bar-container">
                  <div
                    className="language-bar language-bar-conversational"
                    style={{ width: "65%" }}
                  ></div>
                </div>
              </div>

              <div className="language-item">
                <span className="language-name">Deutsch</span>
                <div className="language-bar-container">
                  <div
                    className="language-bar language-bar-conversational"
                    style={{ width: "65%" }}
                  ></div>
                </div>
              </div>

              <div className="language-item">
                <span className="language-name">Nederlands</span>
                <div className="language-bar-container">
                  <div
                    className="language-bar language-bar-conversational"
                    style={{ width: "60%" }}
                  ></div>
                </div>
              </div>

              <div className="language-item">
                <span className="language-name">རྫོང་ཁ</span>
                <div className="language-bar-container">
                  <div
                    className="language-bar language-bar-basic"
                    style={{ width: "40%" }}
                  ></div>
                </div>
              </div>

              <div className="language-item">
                <span className="language-name">Wolof</span>
                <div className="language-bar-container">
                  <div
                    className="language-bar language-bar-basic"
                    style={{ width: "40%" }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-cta">
            <h2>Let's Build Something Real</h2>
            <p>
              Working on AI products that solve actual problems? Let's talk.
            </p>
            <a
              href="mailto:seancafferty1@gmail.com"
              className="btn-primary-modern"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default About;
